<template>
  <div>
    <label class="file-select" v-show="isMultiple || !allFiles.length">
      <div class="upload-cover" v-show="!isUploading">
        <div class="file-upload-icon" :class="{ disabled: disabled }">
          <v-img
            src="@/assets/svg/file/empty-file.svg"
            class="file-icon"
            alt="File Icon"
          />
        </div>
        <div>
          <div class="file-upload-title" :class="{ disabled: disabled }">
            Upload File
          </div>
          <div class="file-upload-subtitle">
            {{ hintText }}
          </div>
        </div>
      </div>
      <div class="uppy-input" :id="`item-${id}`"></div>
      <div v-if="showValidationErrors && !allFiles.length" class="text-error">
        This field is required
      </div>
      <div class="pt-3" :id="`progress-${id}`"></div>
    </label>
    <div class="pb-3" v-if="showFiles">
      <FileComponent
        :isRemoving="isLoading"
        class="py-2"
        v-for="file in allFiles"
        :key="file.id"
        :customActions="customActions"
        :disablePreview="disablePreview"
        :file="file"
        :disabled="disabled"
        @removeFile="handleRemoveFile"
        @show="onShow"
      />
    </div>
  </div>
</template>

<script>
import Uppy from "@uppy/core";
import FileInput from "@uppy/file-input";
import StatusBar from "@uppy/status-bar";
import XHRUpload from "@uppy/xhr-upload";
import Tus from "@uppy/tus";
import "@uppy/core/dist/style.css";
import "@uppy/status-bar/dist/style.css";
import { POST_GLOBAL_MESSAGE } from "@/store/modules/global/action-types";
import { mapActions } from "vuex";
import { size } from "lodash";
import { getToken, getImpersonateUserId } from "@/services/utils";
import FileComponent from "@/components/common/FileComponent";
import { getFileTypesName } from "@/utils/files";
import generateRandomId from "uuid/v4";

export default {
  props: {
    isMultiple: {
      type: Boolean,
      required: false,
      default: false
    },
    limit: {
      type: Number,
      required: false,
      default: 6 // default 1MB
    },
    /**
     * Minimum number of files
     */
    min: {
      type: Number,
      required: false,
      default: null // default unset
    },
    /**
     * Maximum number of files
     */
    max: {
      type: Number,
      required: false,
      default: null // default unset
    },
    /**
     * Array of allowed file types
     * exact mime types image/jpeg, or file extensions .jpp
     * @example
     * ['image/*', '.jpg', '.jpeg', '.png', '.gif']
     */
    fileTypes: {
      type: Array,
      required: false,
      default: null
    },
    files: {
      type: Array,
      required: false,
      default: () => []
    },
    fullWidth: Boolean,
    customActions: Boolean,
    uploadingUrl: String,
    isLoading: Boolean,
    disableAutoProceed: Boolean,
    showValidationErrors: Boolean,
    disabled: Boolean,
    showPreview: Boolean
  },
  components: {
    FileComponent
  },
  data() {
    return {
      id: generateRandomId(),
      currentFile: null,
      allFiles: this.files,
      isUploading: false
    };
  },
  computed: {
    disablePreview() {
      if (this.showPreview) {
        return false;
      }
      return this.disableAutoProceed;
    },
    showFiles() {
      return this.allFiles.length > 0;
    },
    hintText() {
      if (size(this.fileTypes)) {
        return `Must be ${getFileTypesName(this.fileTypes)} and less than ${
          this.limit
        } MB`;
      }

      return `Must be less than ${this.limit} MB`;
    }
  },
  mounted() {
    if (!this.disabled) {
      this.adjustUppyInstance();
    }
  },
  methods: {
    ...mapActions({
      postGlobalMessage: POST_GLOBAL_MESSAGE
    }),
    addFile(file) {
      if (this.disableAutoProceed) {
        this.allFiles = [...this.allFiles, file];
        this.$emit("selectFile", {
          uppyInstance: this.uppy,
          file
        });
      }
    },
    adjustUppyInstance() {
      const uppy = new Uppy({
        id: this.id,
        debug: true,
        autoProceed: !this.disableAutoProceed,
        allowMultipleUploads: this.isMultiple,
        restrictions: {
          maxFileSize: this.limit * 1024 * 1024,
          maxNumberOfFiles: this.isMultiple ? this.max : 1,
          minNumberOfFiles: this.min,
          allowedFileTypes: this.fileTypes
        },
        locale: {
          strings: {
            youCanOnlyUploadX: {
              0: "You can only upload %{smart_count} file",
              1: "You can only upload %{smart_count} files"
            },
            youHaveToAtLeastSelectX: {
              0: "You have to select at least %{smart_count} file",
              1: "You have to select at least %{smart_count} files"
            },
            exceedsSize: "The maximum size alllowed is",
            youCanOnlyUploadFileTypes:
              "The file you selected is not supported format"
          }
        }
      })
        .use(FileInput, { target: `#item-${this.id}`, pretty: false })
        .use(StatusBar, {
          hideCancelButton: this.disableAutoProceed,
          target: `#progress-${this.id}`,
          hideUploadButton: this.customActions,
          hideAfterFinish: true
        });
      if (this.customActions) {
        const token = getToken();
        const impersonateUserId = getImpersonateUserId();
        const headers = {
          Authorization: `Bearer ${token}`
        };
        if (impersonateUserId) {
          headers["X-Impersonate"] = impersonateUserId;
        }
        uppy.use(XHRUpload, {
          fieldName: "file",
          endpoint: this.uploadingUrl,
          method: "post",
          headers,
          limit: 10
        });
      } else {
        // should be removed when all the views will have implemented custom actions
        uppy.use(Tus, { endpoint: "https://master.tus.io/files/", limit: 10 });
      }
      uppy
        .on("upload", this.onUpload)
        .on("file-added", this.addFile)
        .on("upload-success", this.onSuccess)
        .on("file-removed", this.onComplete)
        .on("complete", () => {
          this.onComplete();
          uppy.reset();
        })
        .on("restriction-failed", this.onRestrictionFailed)
        .on("reset-progress", this.onResetProgress)
        .on("upload-error", this.onUploadError);
      this.uppy = uppy;
    },
    onSuccess(file, response) {
      if (this.customActions) {
        file = response.body.data;
      } else {
        // should be removed when all the views will have implemented custom actions
        file.url = response.uploadURL;
      }
      this.allFiles = [...this.allFiles, file];
      this.isUploading = false;
      this.$emit("onUploadSuccess", this.allFiles);
    },
    onUpload() {
      this.isUploading = true;
      if (!this.isMultiple) {
        this.allFiles = [];
      }
    },
    onComplete() {
      this.isUploading = false;
    },
    onUploadError(file) {
      const message = {
        snackbar: true,
        type: "error",
        title: `An error occurred uploading file ${file.name}`
      };
      this.postGlobalMessage(message);
    },
    onRestrictionFailed(file, err) {
      const message = {
        snackbar: true,
        type: "error",
        title: err
      };
      this.postGlobalMessage(message);
    },
    handleRemoveFile(file) {
      if (this.customActions && !this.disableAutoProceed) {
        this.$emit("removeFile", { uppyInstance: this.uppy, file });
      }
      if (this.disableAutoProceed) {
        this.$emit("unselectFile", { uppyInstance: this.uppy, file });
      }
      if (this.isMultiple) {
        this.$forceUpdate();
      }
    },
    onShow(file) {
      this.$emit("show", file);
    },
    setFile(file) {
      this.uppy.addFile(file);
    }
  },
  watch: {
    files(newValue) {
      this.allFiles = newValue;
    }
  }
};
</script>

<style lang="scss">
.text-error {
  font-size: 1rem;
  padding: 12px 10px;
  color: $white;
  font-weight: bold;
  background-color: $error;
  border-radius: 0px 0px 4px 4px;
}
.file-select {
  position: relative;

  .upload-cover {
    cursor: pointer;
    align-items: center;
    display: flex;

    .file-upload-icon {
      font-size: 2rem;
      width: 1rem;
      margin-right: 15px;
      margin-bottom: 10px;
    }

    .file-upload-title {
      color: $primary;
      font-weight: bold;
    }

    .disabled {
      color: $secondary-text !important;
    }
  }

  p {
    font-style: normal;
    font-weight: normal;
    font-size: 14px;
    line-height: 23px;
    color: $secondary-text;
  }
}

.v-input.theme--light.v-text-field.v-text-field--is-booted.v-file-input {
  height: 0px;
  position: absolute;
  width: 0;
}

.v-file-input {
  .v-text-field__slot {
    display: none !important;
  }

  .v-input__prepend-outer {
    display: none !important;
  }

  .v-input__icon.v-input__icon--clear {
    display: none !important;
  }
}

.uppy-input {
  display: none;
}

.uppy-StatusBar {
  background-color: #fafafa;
}
</style>
